/**
 * Browser client for the gateway.
 *
 * Responses are parsed with the shared zod schemas, so a contract drift shows
 * up as a thrown error naming the field rather than as `undefined` rendering
 * into a table cell.
 */
import {
  BarsResponse, CalibrationRun, Expiry, FiredSignal, Health, NewsFeed,
  EventsResponse, IndicatorCatalogue, IndicatorRun, IngestResponse, OptionChain,
  OptionSuggestion, ScoreResponse,
  SourcesStatus, PineRunResponse, Quote, Research, Script, SymbolDetail,
  SentimentSummary, SignalDefinition, Structure, SymbolHit, ValidateResponse,
  VrpResponse,
} from "@tt/shared";
import { z } from "zod";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** A located Pine error, when the failure was the user's script. */
  get pineError(): { message: string; line: number; col: number; kind: string; hint?: string | null } | null {
    const d = this.detail as Record<string, unknown> | undefined;
    if (d && typeof d.message === "string" && typeof d.line === "number") {
      return d as { message: string; line: number; col: number; kind: string; hint?: string | null };
    }
    return null;
  }
}

/**
 * `z.ZodType<T>` would default zod's Input parameter to `T` as well. For any
 * schema using `.default()` the input and output shapes differ, and TS is then
 * free to bind `T` to the input shape -- making every defaulted field optional
 * at the call site even though `parse` always fills it. Leaving Input as
 * `unknown` pins `T` to the parsed output.
 */
async function call<T>(
  path: string,
  schema: z.ZodType<T, z.ZodTypeDef, unknown> | null,
  init?: RequestInit & { signal?: AbortSignal },
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init?.headers } : init?.headers,
  });
  const text = await res.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    // Non-JSON almost always means the request didn't reach the engine --
    // Cloudflare 502 pages, gateway restarts, or the tunnel briefly dropping.
    // Surface the HTTP status so the user can tell "engine is down" from
    // "my request was malformed", and keep a slice of the body for diagnosis.
    const detail = text.slice(0, 300);
    const label = res.status >= 500
      ? `engine unreachable (${res.status})`
      : res.status === 0 || res.status === 408
        ? "request timed out"
        : `non-JSON response from ${path}`;
    throw new ApiError(label, res.status, detail);
  }
  if (!res.ok) {
    const p = payload as { error?: string; detail?: unknown } | null;
    throw new ApiError(p?.error ?? `${res.status} on ${path}`, res.status, p?.detail ?? p);
  }
  if (!schema) return payload as T;
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(
      `response for ${path} did not match the expected shape`,
      502,
      parsed.error.format(),
    );
  }
  return parsed.data;
}

const post = (body: unknown) => ({ method: "POST", body: JSON.stringify(body) });

const AuthUser = z.object({
  id: z.number(),
  email: z.string(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  name: z.string().nullable(),
  created_at: z.string(),
  /** Operator account: sees the data-source diagnostics and the admin page. */
  is_admin: z.boolean().default(false),
});
export type AuthUser = z.infer<typeof AuthUser>;

const AdminUserRow = z.object({
  id: z.number(),
  email: z.string(),
  name: z.string().nullable(),
  created_at: z.string(),
  verified: z.boolean(),
  locked: z.boolean(),
  failed_logins: z.number(),
  last_login: z.string().nullable(),
  last_session: z.string().nullable(),
  is_admin: z.boolean(),
});

const AdminOverview = z.object({
  users: z.number(),
  verified: z.number(),
  unverified: z.number(),
  new_this_week: z.number(),
  locked: z.number(),
  active_sessions: z.number(),
  mail_configured: z.boolean(),
});

const VrpRow = z.object({
  symbol: z.string(),
  error: z.string().optional(),
  observations: z.number().optional(),
  implied_exceeded_realised: z.number().optional(),
  hit_rate: z.number().nullable().optional(),
  ci_low: z.number().nullable().optional(),
  ci_high: z.number().nullable().optional(),
  mean_premium: z.number().nullable().optional(),
  reading: z.string().optional(),
  direction: z.string().nullable().optional(),
});

const MarketRow = z.object({
  symbol: z.string(),
  last: z.number(),
  realised_20d: z.number(),
  realised_60d: z.number(),
  expansion: z.number(),
  chain_iv: z.number(),
  strikes: z.number(),
  gap: z.number(),
});

const TrackRecord = z.object({
  flagged: z.number(),
  resolved: z.number(),
  iv_rose: z.number(),
  realised_fell: z.number(),
  still_open: z.number(),
  since: z.string().nullable(),
  enough_to_judge: z.boolean(),
});

const MarketScan = z.object({
  rows: z.array(MarketRow),
  track_record: TrackRecord.nullable().optional(),
  scanned: z.number().optional(),
  liquid: z.number().optional(),
  measured: z.number().optional(),
  took_ms: z.number().optional(),
  as_of: z.string().optional(),
  caveat: z.string().optional(),
  error: z.string().optional(),
});

const ScreenRow = z.object({
  symbol: z.string(),
  articles: z.number(),
  sentiment: z.number().nullable(),
  realised_vol_20d: z.number().nullable(),
  vol_expanding: z.number().nullable(),
  relative_volume: z.number().nullable(),
  atm_iv: z.number().nullable(),
  iv_less_rv: z.number().nullable(),
});

const ScreenResponse = z.object({
  days: z.number().optional(),
  rows: z.array(ScreenRow),
  caveat: z.string().optional(),
  note: z.string().optional(),
});

const OptionsResearchResponse = z.object({
  horizon_days: z.number(),
  rows: z.array(VrpRow),
  pending: z.object({
    symbols: z.array(z.object({
      symbol: z.string(), days: z.number(),
      since: z.string().nullable(), ready_at: z.number(),
    })),
  }),
  caveat: z.string(),
});

const PasswordPolicy = z.object({
  min_length: z.number(),
  max_length: z.number(),
  rules: z.array(z.string()),
});
export type PasswordPolicy = z.infer<typeof PasswordPolicy>;

const AuthResult = z.object({
  ok: z.boolean(),
  message: z.string().optional(),
  warning: z.string().optional(),
});

export const api = {
  health: (signal?: AbortSignal) =>
    call("/health", z.object({
      gateway: z.string(),
      engine: Health.nullable(),
      engine_error: z.string().optional(),
    }), { signal }),

  bars: (
    symbol: string, timeframe: string, limit = 800,
    extendedHours = false, signal?: AbortSignal,
  ) =>
    call(
      `/bars/${encodeURIComponent(symbol)}?timeframe=${timeframe}&limit=${limit}` +
        `&extended_hours=${extendedHours}`,
      BarsResponse,
      { signal },
    ),

  quote: (symbol: string, signal?: AbortSignal) =>
    call(`/quote/${encodeURIComponent(symbol)}`, Quote, { signal }),

  quotes: (symbols: string[], signal?: AbortSignal) =>
    call(
      `/quotes?symbols=${encodeURIComponent(symbols.join(","))}`,
      z.object({ quotes: z.record(Quote), unavailable: z.array(z.string()) }),
      { signal },
    ),

  search: (q: string, signal?: AbortSignal) =>
    call(`/search?q=${encodeURIComponent(q)}`, z.object({ results: z.array(SymbolHit) }), { signal }),

  watchlists: (signal?: AbortSignal) =>
    call("/watchlists", z.object({
      watchlists: z.array(z.object({
        id: z.number(), name: z.string(), symbols: z.array(z.string()),
      })),
    }), { signal }),

  saveWatchlist: (name: string, symbols: string[]) =>
    call(`/watchlists/${encodeURIComponent(name)}`, z.object({
      id: z.number(), name: z.string(), symbols: z.array(z.string()),
    }), { method: "PUT", body: JSON.stringify({ symbols }) }),

  validatePine: (source: string, signal?: AbortSignal) =>
    call("/pine/validate", ValidateResponse, { ...post({ source }), signal }),

  runPine: (
    body: { source: string; symbol: string; timeframe: string; bars: number; inputs: Record<string, unknown> },
    signal?: AbortSignal,
  ) => call("/pine/run", PineRunResponse, { ...post(body), signal }),

  backtest: (body: Record<string, unknown>, signal?: AbortSignal) =>
    call("/backtest", null, { ...post(body), signal }),

  scripts: (signal?: AbortSignal) =>
    call("/pine/scripts", z.object({ scripts: z.array(Script) }), { signal }),

  saveScript: (body: { name: string; source: string; kind: string; language?: string }) =>
    call("/pine/scripts", Script, post(body)),

  deleteScript: (id: number) =>
    call(`/pine/scripts/${id}`, null, { method: "DELETE" }),

  pineReference: (signal?: AbortSignal) =>
    call<PineReference>("/pine/reference", null, { signal }),

  signalDefinitions: (signal?: AbortSignal) =>
    call("/signals/definitions", z.object({
      signals: z.array(SignalDefinition), note: z.string(),
    }), { signal }),

  calibrate: (
    body: { symbols: string[]; timeframe: string; horizons: number[]; lookback_bars: number },
    signal?: AbortSignal,
  ) => call("/signals/calibrate", CalibrationRun, { ...post(body), signal }),

  latestSignals: (
    body: { symbols: string[]; timeframe: string; within_bars: number },
    signal?: AbortSignal,
  ) => call("/signals/latest", z.object({
    signals: z.array(FiredSignal), count: z.number(),
    with_measured_edge: z.number(), note: z.string(),
  }), { ...post(body), signal }),

  news: (symbol: string | null, days = 14, limit = 60, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ days: String(days), limit: String(limit) });
    if (symbol) qs.set("symbol", symbol);
    return call(`/news?${qs}`, NewsFeed, { signal });
  },

  ingestNews: (body: { symbols: string[]; days: number; include_filings: boolean }) =>
    call("/news/ingest", IngestResponse, post(body)),

  scoreNews: (body: { limit: number }) => call("/news/score", ScoreResponse, post(body)),

  sentiment: (symbol: string, days = 30, signal?: AbortSignal) =>
    call(`/news/sentiment/${encodeURIComponent(symbol)}?days=${days}`, SentimentSummary, { signal }),

  calibrateNews: (body: Record<string, unknown>, signal?: AbortSignal) =>
    call("/news/calibrate", null, { ...post(body), signal }),

  // ── accounts ──
  // No token is handled here: the gateway moves it into an httpOnly cookie
  // the browser sends on its own, so there is nothing for this client to
  // store and nothing for an injected script to read.
  login: (email: string, password: string, remember: boolean) =>
    call("/auth/login", AuthResult, post({ email, password, remember })),

  register: (body: {
    email: string;
    password: string;
    confirm_password: string;
    first_name?: string;
    last_name?: string;
    remember: boolean;
  }) => call("/auth/register", AuthResult, post(body)),

  // The rules come from the module that enforces them, so the form cannot
  // list a requirement the server does not have, or omit one it does.
  passwordPolicy: (signal?: AbortSignal) =>
    call("/auth/policy", PasswordPolicy, { signal }),

  logout: () => call("/auth/logout", AuthResult, post({})),

  me: (signal?: AbortSignal) =>
    call("/auth/me", z.object({ user: AuthUser }), { signal }),

  marketScan: (force = false, signal?: AbortSignal) =>
    call(`/research/market?force=${force}`, MarketScan, { signal }),

  newsScreen: (signal?: AbortSignal) =>
    call("/research/screen?days=7", ScreenResponse, { signal }),

  optionsResearch: (signal?: AbortSignal) =>
    call("/research/options", OptionsResearchResponse, { signal }),

  adminOverview: (signal?: AbortSignal) =>
    call("/admin/overview", AdminOverview, { signal }),
  adminUsers: (signal?: AbortSignal) =>
    call("/admin/users", z.object({ users: z.array(AdminUserRow) }), { signal }),
  adminReset: (id: number) =>
    call(`/admin/users/${id}/reset`, z.object({
      ok: z.boolean(), sent: z.boolean(), link: z.string().nullable(),
    }), { method: "POST", body: "{}" }),
  adminUnlock: (id: number) =>
    call(`/admin/users/${id}/unlock`, z.object({ ok: z.boolean() }), {
      method: "POST", body: "{}",
    }),
  adminDelete: (id: number) =>
    call(`/admin/users/${id}`, z.object({ ok: z.boolean(), deleted: z.string() }), {
      method: "DELETE",
    }),

  verifyEmail: (token: string) =>
    call("/auth/verify", AuthResult, post({ token })),

  resendVerification: (email: string) =>
    call("/auth/resend-verification", AuthResult, post({ email })),

  requestReset: (email: string) =>
    call("/auth/request-reset", AuthResult, post({ email })),

  resetPassword: (token: string, password: string) =>
    call("/auth/reset", AuthResult, post({ token, password })),

  symbolDetail: (symbol: string, signal?: AbortSignal) =>
    call(`/symbol/${encodeURIComponent(symbol)}`, SymbolDetail, { signal }),

  research: (symbol: string, timeframe = "1d", signal?: AbortSignal) =>
    call(`/research/${encodeURIComponent(symbol)}?timeframe=${timeframe}`, Research, { signal }),

  // ── options ──
  expiries: (symbol: string, signal?: AbortSignal) =>
    call(
      `/options/expiries/${encodeURIComponent(symbol)}`,
      z.object({ symbol: z.string(), expiries: z.array(Expiry) }),
      { signal },
    ),

  chain: (symbol: string, expiry: string | null, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ max_strikes: "80" });
    if (expiry) qs.set("expiry", expiry);
    return call(`/options/chain/${encodeURIComponent(symbol)}?${qs}`, OptionChain, { signal });
  },

  structures: (symbol: string, view: string, expiry: string | null, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ view });
    if (expiry) qs.set("expiry", expiry);
    return call(
      `/options/structures/${encodeURIComponent(symbol)}?${qs}`,
      z.object({
        symbol: z.string(), spot: z.number(), expiry: z.string(),
        days_to_expiry: z.number(), atm_iv: z.number().nullable(),
        expected_move: z.number().nullable(),
        structures: z.array(Structure), note: z.string(),
      }),
      { signal },
    );
  },

  optionSuggest: (symbol: string, expiry: string | null, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (expiry) qs.set("expiry", expiry);
    return call(
      `/options/suggest/${encodeURIComponent(symbol)}?${qs}`,
      OptionSuggestion,
      { signal },
    );
  },

  events: (symbol: string, days = 730, signal?: AbortSignal) =>
    call(`/events/${encodeURIComponent(symbol)}?days=${days}`, EventsResponse, { signal }),

  sources: (signal?: AbortSignal) => call("/sources", SourcesStatus, { signal }),

  timeframes: (signal?: AbortSignal) =>
    call("/timeframes", z.object({
      timeframes: z.array(z.object({
        id: z.string(),
        seconds: z.number(),
        label: z.string(),
        group: z.string(),
        native: z.array(z.string()),
        derive_from: z.string().nullable(),
        intraday: z.boolean(),
        available: z.boolean(),
        fetched_as: z.string().optional(),
        derived: z.boolean().optional(),
        reason: z.string().optional(),
      })),
      providers: z.array(z.string()),
      note: z.string(),
    }), { signal }),

  // ── indicators ──
  indicatorCatalogue: (signal?: AbortSignal) =>
    call("/indicators/catalogue", IndicatorCatalogue, { signal }),

  runIndicators: (
    body: {
      symbol: string; timeframe: string; bars: number;
      instances: { instance_id: string; indicator_id: string; params: Record<string, unknown> }[];
    },
    signal?: AbortSignal,
  ) => call("/indicators/run", IndicatorRun, { ...post(body), signal }),

  vrp: (symbol: string, horizonDays = 30, signal?: AbortSignal) =>
    call(
      `/options/vrp/${encodeURIComponent(symbol)}?horizon_days=${horizonDays}`,
      VrpResponse,
      { signal },
    ),
};

export interface PineReference {
  version: number;
  functions: Record<string, string[]>;
  variables: string[];
  constants: string[];
  unsupported: { feature: string; reason: string }[];
}
